(() => {
  const config = document.querySelector('[data-ew-currency-config]');
  if (!config || config.dataset.base !== 'USD' || window.EthnicWishCurrency) return;
  const supported = ['GBP', 'USD', 'INR', 'AUD', 'NZD', 'CAD', 'AED', 'EUR', 'CHF', 'LKR', 'ZAR'];
  const preferenceKey = 'ethnicwish-display-currency-v1';
  const ratesKey = 'ethnicwish-usd-rates-v1';
  const countries = { US: 'USD', GB: 'GBP', GG: 'GBP', JE: 'GBP', IM: 'GBP', IN: 'INR', AU: 'AUD', NZ: 'NZD', CA: 'CAD', AE: 'AED', CH: 'CHF', LI: 'CHF', LK: 'LKR', ZA: 'ZAR' };
  'AT BE BG HR CY EE FI FR DE GR IE IT LV LT LU MT NL PT SK SI ES AD MC SM VA AX'.split(' ').forEach(code => { countries[code] = 'EUR'; });
  const read = key => { try { return JSON.parse(localStorage.getItem(key)); } catch (_) { return null; } };
  const save = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch (_) {} };
  const validPreference = value => supported.includes(value) ? value : 'auto';
  let preference = validPreference(read(preferenceKey));
  let detected = 'USD';
  let active = 'USD';
  let rate = 1;
  let generation = 0;
  let pendingRates;
  let detection;
  let status = '';
  let cache = read(ratesKey);
  const locale = document.documentElement.lang || 'en';
  const formatters = new Map();
  const validRate = row => row?.base === 'USD' && supported.includes(row.quote) && Number.isFinite(row.rate) && row.rate > 0 && Number.isFinite(Date.parse(row.date)) && Date.now() - Date.parse(row.date) < 7 * 86400000 && Date.parse(row.date) <= Date.now() + 86400000;
  const freshCache = () => cache && Number.isFinite(cache.saved) && Date.now() >= cache.saved && Date.now() - cache.saved < 12 * 3600000 && Array.isArray(cache.rows);
  const fetchJSON = async (url, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      if (!response.ok) throw new Error('Unavailable');
      return await response.json();
    } finally { clearTimeout(timer); }
  };
  const getRates = () => {
    if (freshCache()) return Promise.resolve(cache.rows);
    if (!pendingRates) {
      pendingRates = fetchJSON(`https://api.frankfurter.dev/v2/rates?base=USD&quotes=${supported.filter(code => code !== 'USD').join(',')}`, { credentials: 'omit', referrerPolicy: 'no-referrer' })
        .then(rows => {
          if (!Array.isArray(rows)) throw new Error('Invalid rates');
          const valid = rows.filter(validRate);
          if (!valid.length) throw new Error('No recent rates');
          cache = { saved: Date.now(), rows: valid };
          save(ratesKey, cache);
          return valid;
        }).finally(() => { pendingRates = null; });
    }
    return pendingRates;
  };
  const detect = () => {
    if (!detection) {
      const root = window.Shopify?.routes?.root || '/';
      detection = fetchJSON(`${root}browsing_context_suggestions.json?country[enabled]=true`, { credentials: 'same-origin' })
        .then(data => { detected = countries[data?.detected_values?.country?.handle] || 'USD'; })
        .catch(() => { detected = 'USD'; });
    }
    return detection;
  };
  const format = cents => {
    if (!formatters.has(active)) formatters.set(active, new Intl.NumberFormat(locale, { style: 'currency', currency: active, currencyDisplay: 'code' }));
    return formatters.get(active).format(Number(cents) / 100 * rate);
  };
  const each = (root, selector, callback) => {
    if (root.matches?.(selector)) callback(root);
    root.querySelectorAll?.(selector).forEach(callback);
  };
  const priceFilters = new WeakMap();
  const refreshPriceFilters = root => each(root, '[data-ew-price-range]', range => {
    let fields = priceFilters.get(range);
    if (!fields) {
      fields = Array.from(range.querySelectorAll('input[type="number"]')).map(input => {
        const base = document.createElement('input');
        base.type = 'hidden';
        base.name = input.name;
        base.value = input.value;
        input.removeAttribute('name');
        input.insertAdjacentElement('afterend', base);
        const field = { input, base, maximum: Number(input.max), placeholder: Number(input.placeholder), rate: 1 };
        input.addEventListener('input', () => {
          if (input.value === '' || !Number.isFinite(input.valueAsNumber)) { base.value = ''; return; }
          const cents = input.valueAsNumber / field.rate * 100;
          const rounded = base.name.endsWith('.gte') ? Math.floor(cents + 1e-7) : Math.ceil(cents - 1e-7);
          base.value = (rounded / 100).toFixed(2);
        });
        return field;
      });
      priceFilters.set(range, fields);
    }
    range.querySelectorAll('[data-ew-filter-currency]').forEach(label => {
      if (label.textContent !== active) label.textContent = active;
    });
    fields.forEach(field => {
      if (field.rate === rate && field.currency === active) return;
      field.input.max = (Math.ceil(field.maximum * rate * 100) / 100).toFixed(2);
      field.input.placeholder = (Math.ceil(field.placeholder * rate * 100) / 100).toFixed(2);
      field.input.value = field.base.value === '' ? '' : (Number(field.base.value) * rate).toFixed(2);
      field.rate = rate;
      field.currency = active;
    });
  });
  const refresh = (root = document) => {
    refreshPriceFilters(root);
    each(root, '[data-ew-money]', node => {
      const cents = node.dataset.ewMoney;
      if (cents === '' || !Number.isFinite(Number(cents))) return;
      const text = format(cents);
      if (node.textContent !== text) node.textContent = text;
    });
    each(root, '[data-ew-currency-control]', node => { node.hidden = false; });
    each(root, '[data-ew-currency-select]', node => {
      node.value = preference;
      const automatic = node.querySelector('[value="auto"]');
      if (automatic) automatic.textContent = `${config.dataset.autoLabel} · ${active}`;
    });
    each(root, '[data-ew-local-only]', node => { node.hidden = active === 'USD'; });
    each(root, '[data-ew-currency-status]', node => {
      node.hidden = !status;
      if (node.textContent !== status) node.textContent = status;
    });
  };
  const choose = async () => {
    const token = ++generation;
    status = '';
    refresh();
    if (preference === 'auto' && config.dataset.detect === 'true') await detect();
    if (token !== generation) return;
    const target = preference === 'auto' ? detected : preference;
    let nextRate = 1;
    try {
      if (target !== 'USD') {
        const rows = await getRates();
        const row = rows.find(item => item.quote === target && validRate(item));
        if (!row) throw new Error('Missing rate');
        nextRate = row.rate;
      }
      if (token !== generation) return;
      active = target;
      rate = nextRate;
    } catch (_) {
      if (token !== generation) return;
      active = 'USD';
      rate = 1;
      status = config.dataset.unavailable;
    }
    refresh();
  };
  window.EthnicWishCurrency = { refresh };
  document.addEventListener('change', event => {
    if (!event.target.matches('[data-ew-currency-select]')) return;
    preference = validPreference(event.target.value);
    save(preferenceKey, preference);
    choose();
  });
  window.addEventListener('storage', event => {
    if (event.key !== preferenceKey && event.key !== null) return;
    preference = validPreference(read(preferenceKey));
    choose();
  });
  new MutationObserver(records => {
    records.forEach(record => {
      if (record.type === 'attributes') refresh(record.target);
      else record.addedNodes.forEach(node => { if (node.nodeType === 1) refresh(node); });
    });
  }).observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['data-ew-money'] });
  choose();
})();
